import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'

// ============================================================
// useGigs.js
//
// WHAT THIS FILE DOES
//
// This is the main hook for managing gig data. It is the
// single source of truth for the gigs list across the whole
// app. Any page that needs to read, create, edit, or delete
// gigs imports this hook.
//
// WHAT IT GIVES YOU
//   gigs       -- the list of gig objects for the current user
//   loading    -- true while gigs are being fetched
//   error      -- the last error message, or null
//   addGig     -- save a new gig
//   updateGig  -- edit an existing gig
//   deleteGig  -- remove a gig
//   fetchGigs  -- manually reload the list
//
// NOTIFICATION SCHEDULING
//   After a gig is saved or updated, AddGig calls
//   scheduleForGig() from useScheduleNotifications.
//   That hook writes rows to scheduled_notifications with the
//   exact times for each push notification. A cron job running
//   every minute on the server reads those rows and sends the
//   pushes via OneSignal -- even if the app is never opened
//   again before the gig day.
//
//   deleteGig does NOT need to clean up scheduled_notifications
//   because that table has ON DELETE CASCADE on gig_id, which
//   means the database removes those rows automatically whenever
//   the gig row is deleted.
// ============================================================


// ============================================================
// scheduleNotifications
//
// Calls the Edge Function in 'schedule' mode for a single gig.
// The Edge Function calculates exact get_ready and leave_now
// times using Mapbox drive time, then upserts them into the
// scheduled_notifications table.
//
// This is "fire and forget" -- we call it after the gig is
// saved but we do not wait for it to finish. A failure here
// is logged as a warning but never blocks the gig save itself.
//
// WHY THE ANON KEY AND NOT THE SERVICE ROLE KEY
//   The anon key is safe to use on the client. The Edge
//   Function identifies the user from the userId in the
//   request body, and the service role key stays server-side
//   inside the Edge Function where it cannot be exposed.
// ============================================================
async function scheduleNotifications(gigId, userId) {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
  // the base URL for this Supabase project, like "https://abc.supabase.co"

  const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
  // the public anon key -- safe to use in client-side code

  if (!supabaseUrl || !anonKey) {
    console.warn('scheduleNotifications: missing env vars -- skipping')
    return
    // stop here if the environment variables are not set
    // this prevents a confusing network error during local dev
  }

  const res = await fetch(`${supabaseUrl}/functions/v1/send-gig-reminders`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${anonKey}`,
      // Bearer token format is required by the Edge Function
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ mode: 'schedule', gigId, userId }),
    // mode: 'schedule' tells the Edge Function to calculate and
    // store notification times rather than send a push right now
    // gigId and userId let the Edge Function find the right gig row
    // and the user's home address from notification_preferences
  })

  if (!res.ok) {
    const text = await res.text().catch(() => '')
    // .catch(() => '') = if reading the response body fails, use empty string
    console.warn(`scheduleNotifications: Edge Function returned ${res.status}:`, text)
    // log the status code and any error message from the server
  } else {
    console.log(`scheduleNotifications: scheduled for gig ${gigId}`)
  }
}


// ============================================================
// useGigs HOOK
// ============================================================

export function useGigs() {

  // ---- STATE ----

  const [gigs, setGigs] = useState([])
  // gigs = the array of gig objects loaded from Supabase
  // starts empty, gets filled by fetchGigs below

  const [loading, setLoading] = useState(true)
  // true while a fetch is running
  // pages use this to show a loading spinner or placeholder text

  const [error, setError] = useState(null)
  // null = no problem
  // string = the last error message from any failed operation


  // -------------------------------------------------------
  // Run fetchGigs once when the hook is first used.
  // useEffect with an empty [] array runs only on mount,
  // which is when the page first opens.
  // -------------------------------------------------------
  useEffect(() => {
    fetchGigs()
  }, [])
  // [] = empty dependency array = "run this once and never again"


  // -------------------------------------------------------
  // fetchGigs
  //
  // Loads all gigs for the current user from Supabase,
  // sorted by date so the soonest gig appears first.
  //
  // WHY WE FILTER BY user_id
  //   Row Level Security on the gigs table already restricts
  //   queries to the current user, but we also add .eq()
  //   explicitly as a safety net so the intent is clear in
  //   the code and works even if RLS is ever misconfigured.
  // -------------------------------------------------------
  const fetchGigs = async () => {
    try {
      setLoading(true)
      // show spinner before the request starts

      const { data: { user }, error: userError } = await supabase.auth.getUser()
      // get the currently logged-in user
      // data: { user } = dig into the response and pull out the user object
      // error: userError = rename the error so it does not clash with other
      //   error variables later in this function

      if (userError) throw userError
      // if Supabase could not identify the user, stop here

      if (!user) throw new Error('Not authenticated')
      // extra safety check -- throw a readable error if no session exists

      const { data, error } = await supabase
        .from('gigs')
        // which table to read from
        .select('*')
        // get every column on each row
        .eq('user_id', user.id)
        // only return rows that belong to this user
        .order('date', { ascending: true })
        // sort by the date column, earliest date first
        // ascending: true = oldest/soonest gig at the top of the list

      if (error) throw error
      // if the database query failed, jump to catch

      setGigs(data || [])
      // data || [] = use the returned array, or an empty array if null
      // the empty array covers the case where the user has no gigs yet

    } catch (err) {
      setError(err.message)
      // save the error message so the UI can display it
      console.error('Error fetching gigs:', err)
    } finally {
      setLoading(false)
      // always turn off loading when done, whether it worked or not
      // "finally" runs even if an error was thrown
    }
  }


  // -------------------------------------------------------
  // addGig
  //
  // Saves a new gig row to the database, updates local state,
  // then fires scheduleNotifications so the server calculates
  // and stores the notification times for this gig.
  //
  // gigData = the form values for the new gig, like:
  //   { title, date, venue_name, call_time, gig_time, ... }
  //   user_id is NOT included in gigData -- we add it here
  //   so the calling component never has to know the user ID.
  // -------------------------------------------------------
  const addGig = async (gigData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      // get the logged-in user so we can attach the gig to their account

      if (!user) throw new Error('Not authenticated')

      const { data, error } = await supabase
        .from('gigs')
        .insert([{ ...gigData, user_id: user.id }])
        // { ...gigData } = copy all the form fields into the new row
        // user_id: user.id = add the user's ID so the row is owned by them
        // the whole object is wrapped in [] because .insert() takes an array
        .select()
        // .select() returns the newly created row so we can add it to state
        // without calling fetchGigs() again

      if (error) throw error

      setGigs([...gigs, ...data])
      // ...gigs = keep all existing gigs in the list
      // ...data = spread the new row(s) from the response onto the end
      // result: the full list plus the new gig at the bottom
      // (gigs are sorted by date so it will be in the right place after
      //  the user navigates away and back, but for now it appends)

      // Fire notification scheduling in the background.
      // .catch() means a failure here does not crash the function --
      // the gig was already saved successfully so this is non-fatal.
      scheduleNotifications(data[0].id, user.id).catch(err =>
        console.warn('addGig: notification scheduling failed (non-fatal):', err)
      )
      // data[0].id = the UUID Supabase assigned to the new gig row
      // we need this so the Edge Function knows which gig to schedule for

      return data[0]
      // send the saved gig back to the form so it can show a success state
      // or navigate to the new gig's detail page

    } catch (err) {
      setError(err.message)
      console.error('Error adding gig:', err)
      throw err
      // re-throw so AddGig can catch it and show its own error message
    }
  }


  // -------------------------------------------------------
  // updateGig
  //
  // Changes one or more fields on an existing gig row and
  // updates the matching entry in local state.
  //
  // id      = the UUID of the gig to change
  // updates = an object containing only the changed fields,
  //           like { venue_name: "New Venue", gig_time: "21:00" }
  //           Fields not included in updates are left unchanged.
  //
  // NOTE ABOUT NOTIFICATION RESCHEDULING
  //   This function does NOT call scheduleNotifications().
  //   Rescheduling is handled by scheduleForGig() in
  //   useScheduleNotifications, which AddGig calls right after
  //   updateGig returns. That hook already has the freshly
  //   calculated leave_time and makeup_start_time values and
  //   writes them directly to scheduled_notifications.
  //
  //   Calling scheduleNotifications() here too would create a
  //   race condition where the Edge Function could overwrite the
  //   correct rows with stale data from the database.
  // -------------------------------------------------------
  const updateGig = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('gigs')
        .update(updates)
        // .update() only changes the fields included in the updates object
        // all other fields on the row stay exactly as they were
        .eq('id', id)
        // WHERE id = this specific gig's UUID
        // always required -- without .eq() every row would be updated
        .select()
        // return the updated row so we can sync local state without refetching

      if (error) throw error

      setGigs(gigs.map(g => g.id === id ? data[0] : g))
      // .map() goes through every gig in the list and returns a new array
      // g.id === id = is this the gig we just updated?
      // ? data[0]   = yes -- replace it with the fresh data from Supabase
      // : g         = no  -- keep the original unchanged
      // result: the same list with only the edited gig replaced in-place

      return data[0]
      // send the updated gig back to the caller

    } catch (err) {
      setError(err.message)
      console.error('Error updating gig:', err)
      throw err
      // re-throw so AddGig can catch it and show an error message
    }
  }


  // -------------------------------------------------------
  // deleteGig
  //
  // Removes a gig row from the database and removes it from
  // local state so the UI updates instantly.
  //
  // id = the UUID of the gig to delete
  //
  // NO NOTIFICATION CLEANUP NEEDED
  //   The scheduled_notifications table has an ON DELETE CASCADE
  //   constraint on its gig_id column. That means the database
  //   automatically deletes any notification rows that belong
  //   to this gig when the gig itself is deleted. We do not
  //   need to write any extra cleanup code here.
  // -------------------------------------------------------
  const deleteGig = async (id) => {
    try {
      const { error } = await supabase
        .from('gigs')
        .delete()
        .eq('id', id)
        // DELETE FROM gigs WHERE id = the given UUID
        // .eq() is critical -- without it, all rows would be deleted

      if (error) throw error

      setGigs(gigs.filter(g => g.id !== id))
      // .filter() returns a new array that only keeps items where the
      // callback returns true
      // g.id !== id = keep every gig whose ID does NOT match the deleted one
      // result: the full list minus the removed gig
      // scheduled_notifications rows for this gig are removed automatically
      // by the ON DELETE CASCADE constraint in the database

    } catch (err) {
      setError(err.message)
      console.error('Error deleting gig:', err)
      throw err
      // re-throw so the calling component can show a confirmation or error
    }
  }


  // Return everything the app needs from this hook
  return {
    gigs,       // Gig[] -- array of gig objects for the current user
    loading,    // boolean -- true while fetchGigs is running
    error,      // string | null -- last error message
    addGig,     // (gigData: object) => Promise<Gig>
    updateGig,  // (id: string, updates: object) => Promise<Gig>
    deleteGig,  // (id: string) => Promise<void>
    fetchGigs,  // () => Promise<void> -- call to manually reload the list
  }
}